import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { 
  Mail, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Search, 
  CheckCircle, 
  XCircle, 
  Calendar, 
  Star, 
  Filter,
  MessageCircle,
  User,
  ArrowLeft,
  Trash,
  Archive
} from 'lucide-react';
import { 
  getProfessorInteractions, 
  getProfessorReplies, 
  markReplyAsRead 
} from '../lib/api';
import { formatDistanceToNow, format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { ProfessorInteraction } from '../lib/types';
import { CustomDropdown } from '../components/CustomDropdown';

export function ProfessorRepliesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [professors, setProfessors] = useState<ProfessorInteraction[]>([]);
  const [allReplies, setAllReplies] = useState<any[]>([]);
  const [visibleReplies, setVisibleReplies] = useState<any[]>([]);
  const [selectedProfessor, setSelectedProfessor] = useState<string | null>(null);
  const [expandedReply, setExpandedReply] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'read' | 'unread'>('all');
  const [error, setError] = useState<string | null>(null);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [testInteractionId, setTestInteractionId] = useState('');
  const [testReplyContent, setTestReplyContent] = useState('');

  // Load all professors with replies
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const interactions = await getProfessorInteractions();
        // Filter to only professors with 'responded' status
        const respondedProfessors = interactions.filter(
          prof => prof.status === 'responded'
        );
        setProfessors(respondedProfessors);
        
        if (respondedProfessors.length > 0) {
          // Load replies for all professors
          await loadAllReplies(respondedProfessors);
        }
      } catch (err) {
        console.error('Error loading professor data:', err);
        setError('Failed to load professors. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);
  
  async function loadAllReplies(professors: ProfessorInteraction[]) {
    try {
      setRepliesLoading(true);
      const allRepliesData: any[] = [];
      
      // Load replies for each professor
      for (const professor of professors) {
        const replies = await getProfessorReplies(professor.id);
        // Attach professor info to each reply
        const repliesWithProfessor = replies.map(reply => ({
          ...reply,
          professorName: professor.professor_name,
          university: professor.university,
          professorId: professor.id
        }));
        
        allRepliesData.push(...repliesWithProfessor);
      }
      
      // Sort by date, newest first
      allRepliesData.sort((a, b) => 
        new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
      );
      
      setAllReplies(allRepliesData);
      setVisibleReplies(allRepliesData);
    } catch (err) {
      console.error('Error loading replies:', err);
      setError('Failed to load email replies. Please try again.');
    } finally {
      setRepliesLoading(false);
    }
  }
  
  // Filter replies when search term, professor, or read status changes
  useEffect(() => {
    let filtered = [...allReplies];
    
    // Filter by professor
    if (selectedProfessor) {
      filtered = filtered.filter(reply => reply.professorId === selectedProfessor);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(reply => 
        reply.subject?.toLowerCase().includes(term) || 
        reply.body?.toLowerCase().includes(term) ||
        reply.professorName?.toLowerCase().includes(term)
      );
    }
    
    // Filter by read status
    if (filterStatus === 'read') {
      filtered = filtered.filter(reply => reply.read);
    } else if (filterStatus === 'unread') {
      filtered = filtered.filter(reply => !reply.read);
    }
    
    setVisibleReplies(filtered);
  }, [searchTerm, selectedProfessor, filterStatus, allReplies]);
  
  const handleExpandReply = async (replyId: string) => {
    setExpandedReply(expandedReply === replyId ? null : replyId);
    
    // Mark as read if expanding
    if (expandedReply !== replyId) {
      try {
        const reply = allReplies.find(r => r.id === replyId);
        if (reply && !reply.read) {
          await markReplyAsRead(replyId);
          // Update local state to reflect the change
          setAllReplies(allReplies.map(r => 
            r.id === replyId ? { ...r, read: true } : r
          ));
        }
      } catch (error) {
        console.error('Error marking reply as read:', error);
      }
    }
  };
  
  function groupRepliesByDate(replies: any[]) {
    const grouped: Record<string, any[]> = {};
    
    replies.forEach(reply => {
      const date = new Date(reply.received_at);
      let group = 'Today';
      
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const isToday = date.toDateString() === today.toDateString();
      const isYesterday = date.toDateString() === yesterday.toDateString();
      
      if (isToday) {
        group = 'Today';
      } else if (isYesterday) {
        group = 'Yesterday';
      } else {
        group = format(date, 'MMMM d, yyyy');
      }
      
      if (!grouped[group]) {
        grouped[group] = [];
      }
      
      grouped[group].push(reply);
    });
    
    return grouped;
  }
  
  const groupedReplies = groupRepliesByDate(visibleReplies);
  const unreadCount = allReplies.filter(r => !r.read).length;
  
  // Update the header section with proper styling and back button
  return (
    <motion.div 
      className="min-h-screen bg-gray-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header with fixed dark background */}
        <div className="py-6 border-b border-gray-800">
          <div className="flex flex-col space-y-4">
            {/* Title and back button section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-2xl font-bold text-white">Professor Replies</h1>
                  <p className="text-gray-400 text-sm mt-1">
                    {allReplies.length} {allReplies.length === 1 ? 'reply' : 'replies'} from professors
                    {unreadCount > 0 && ` • ${unreadCount} unread`}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {/* Filter toggles */}
                <motion.button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filterStatus === 'all' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  All
                </motion.button>
                <motion.button
                  onClick={() => setFilterStatus('unread')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filterStatus === 'unread' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Unread
                </motion.button>
                <motion.button
                  onClick={() => setFilterStatus('read')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    filterStatus === 'read' 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Read
                </motion.button>
              </div>
            </div>
            
            {/* Search and filter section with proper alignment */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search in replies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full rounded-lg bg-gray-800 border border-gray-700 text-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2.5"
                />
              </div>
              
              <div className="relative w-full sm:w-64">
                <CustomDropdown
                  options={professors.map(prof => prof.professor_name)}
                  value={selectedProfessor ? professors.find(p => p.id === selectedProfessor)?.professor_name || '' : ''}
                  onChange={(value) => {
                    const prof = professors.find(p => p.professor_name === value);
                    setSelectedProfessor(prof ? prof.id : null);
                  }}
                  placeholder="All Professors"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="relative w-12 h-12">
              <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Mail className="h-5 w-5 text-indigo-500" />
              </div>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {error && !loading && (
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-4 max-w-md">
              <div className="flex items-start space-x-3">
                <XCircle className="h-6 w-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-red-300 font-medium">Something went wrong</h3>
                  <p className="text-red-300/80 text-sm mt-1">{error}</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="mt-3 text-sm bg-red-800/30 hover:bg-red-800/50 text-red-300 py-1.5 px-3 rounded-md transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && visibleReplies.length === 0 && (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            <div className="bg-gray-800/50 p-6 rounded-full mb-4">
              <Mail className="h-12 w-12 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No replies found</h3>
            <p className="text-gray-400 max-w-md">
              {allReplies.length > 0 
                ? "Try adjusting your filters to see more results"
                : "You haven't received any replies from professors yet"}
            </p>
          </div>
        )}
        
        {/* Replies List */}
        {!loading && !error && visibleReplies.length > 0 && (
          <div className="py-6 space-y-8">
            <LayoutGroup>
              {Object.entries(groupedReplies).map(([date, replies]) => (
                <motion.div 
                  key={date}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-lg font-medium text-gray-400 mb-3 flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    {date}
                  </h2>
                  <div className="space-y-4">
                    <AnimatePresence>
                      {replies.map((reply, index) => (
                        <motion.div
                          key={reply.id}
                          layout
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ 
                            duration: 0.3,
                            delay: index * 0.05
                          }}
                          className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden"
                        >
                          <div 
                            className={`px-4 py-4 cursor-pointer ${!reply.read ? 'bg-indigo-900/20' : ''}`}
                            onClick={() => handleExpandReply(reply.id)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start space-x-3">
                                <div className="bg-gray-700 rounded-full p-2 mt-1">
                                  <User className="h-5 w-5 text-indigo-400" />
                                </div>
                                <div>
                                  <div className="flex items-center">
                                    {!reply.read && (
                                      <span className="h-2 w-2 rounded-full bg-indigo-500 mr-2"></span>
                                    )}
                                    <h3 className="font-medium text-white">
                                      {reply.professorName || reply.from_name || reply.from_email}
                                    </h3>
                                  </div>
                                  <p className="text-sm text-gray-400 mt-0.5">
                                    {reply.university}
                                  </p>
                                  <p className="text-sm font-medium text-gray-300 mt-2">
                                    {reply.subject}
                                  </p>
                                  {!expandedReply || expandedReply !== reply.id ? (
                                    <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                                      {reply.body?.substring(0, 120)}
                                      {reply.body?.length > 120 ? '...' : ''}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex flex-col items-end">
                                <span className="text-xs text-gray-500">
                                  {formatDistanceToNow(new Date(reply.received_at))} ago
                                </span>
                                <div className="mt-2">
                                  {expandedReply === reply.id ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <AnimatePresence>
                            {expandedReply === reply.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <div className="px-4 pb-4 pt-2 border-t border-gray-700">
                                  <div className="bg-gray-900 rounded-lg p-4 whitespace-pre-wrap text-gray-300 text-sm mb-4">
                                    {reply.body}
                                  </div>
                                  
                                  <div className="flex justify-between items-center">
                                    <p className="text-xs text-gray-500">
                                      Received on {format(new Date(reply.received_at), 'MMM d, yyyy • h:mm a')}
                                    </p>
                                    
                                    <div className="flex space-x-2">
                                      <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
                                      >
                                        <Archive className="h-4 w-4" />
                                      </motion.button>
                                      <motion.button
                                        onClick={() => navigate(`/emails?professorId=${reply.professorId}`)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium flex items-center"
                                      >
                                        <MessageCircle className="h-4 w-4 mr-1.5" />
                                        Reply
                                      </motion.button>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </LayoutGroup>
          </div>
        )}
        
        {/* Development Testing Tool (Visible only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 border border-gray-700 rounded-lg bg-gray-800">
            <h3 className="text-white font-medium mb-3">Development Testing Tool</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 mb-1 text-sm">Professor ID</label>
                <input
                  type="text"
                  className="w-full p-2 bg-gray-900 border border-gray-700 rounded text-white"
                  placeholder="Enter interaction ID"
                  value={testInteractionId}
                  onChange={(e) => setTestInteractionId(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-1 text-sm">Reply Content</label>
                <textarea
                  className="w-full p-2 bg-gray-900 border border-gray-700 rounded text-white min-h-[100px]"
                  placeholder="Enter test reply content"
                  value={testReplyContent}
                  onChange={(e) => setTestReplyContent(e.target.value)}
                />
              </div>
              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                onClick={handleSimulateReply}
              >
                Simulate Reply
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}